import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
  Text,
  Icon,
  FormHelperText,
} from "@chakra-ui/react";
import { PhoneIcon, CalendarIcon } from "@chakra-ui/icons";

const ContactModal = ({ isOpen, onClose, place, onUpdate }) => {
  const [formData, setFormData] = useState({
    contacted: true,
    contact_channel: "",
    contact_person_name: "",
    contact_notes: "",
    should_return_contact: false,
    return_contact_date: "",
    status: "contacted",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Popular dados quando editar lugar existente
  useEffect(() => {
    if (place && isOpen) {
      setFormData({
        contacted: place.contacted || true,
        contact_channel: place.contact_channel || "",
        contact_person_name: place.contact_person_name || "",
        contact_notes: typeof place.contact_notes === 'string' ? place.contact_notes : "",
        should_return_contact: place.should_return_contact || false,
        return_contact_date: place.return_contact_date || "",
        status: place.contacted ? place.status : "contacted",
      });
    }
  }, [place, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const validateForm = () => {
    if (!formData.contact_channel) {
      setError("Canal de contato é obrigatório");
      return false;
    }
    if (!formData.contact_person_name.trim()) {
      setError("Nome da pessoa de contato é obrigatório");
      return false;
    }
    if (formData.should_return_contact && !formData.return_contact_date) {
      setError("Data de retorno é obrigatória quando marcado para retornar");
      return false;
    }
    if (formData.should_return_contact && formData.return_contact_date) {
      const returnDate = new Date(formData.return_contact_date);
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);

      if (returnDate < tomorrow) {
        setError("Data de retorno deve ser a partir de amanhã");
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Garantir que os tipos estejam corretos antes de enviar
      const cleanFormData = {
        ...formData,
        contact_person_name: String(formData.contact_person_name || "").trim(),
        contact_channel: String(formData.contact_channel || ""),
      };

      // Só incluir contact_notes se não estiver vazio
      if (formData.contact_notes && formData.contact_notes.trim()) {
        cleanFormData.contact_notes = String(formData.contact_notes).trim();
      }

      // Só incluir return_contact_date se should_return_contact for true e tiver data válida
      if (formData.should_return_contact && formData.return_contact_date) {
        cleanFormData.should_return_contact = true;
        cleanFormData.return_contact_date = String(formData.return_contact_date);
      } else {
        cleanFormData.should_return_contact = false;
      }

      await onUpdate(cleanFormData);
      handleClose();
    } catch (error) {
      console.error("Erro ao atualizar contato:", error);
      setError(typeof error === 'string' ? error : "Erro ao atualizar contato");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  // Data mínima para retorno (amanhã)
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const contactChannelOptions = [
    { value: "phone", label: "📞 Telefone", icon: "📞" },
    { value: "whatsapp", label: "💬 WhatsApp", icon: "💬" },
    { value: "email", label: "📧 E-mail", icon: "📧" },
    { value: "visit", label: "🚪 Visita Presencial", icon: "🚪" },
    { value: "social_media", label: "📱 Redes Sociais", icon: "📱" },
    { value: "other", label: "💬 Outros", icon: "💬" },
  ];

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={PhoneIcon} color="blue.500" />
            <Text>Registrar Contato</Text>
          </HStack>
          <Text fontSize="sm" color="gray.600" mt={1}>
            {place?.title}
          </Text>
        </ModalHeader>
        <ModalCloseButton isDisabled={loading} />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <FormControl isRequired>
              <FormLabel>Canal de Contato</FormLabel>
              <Select
                placeholder="Selecione o canal utilizado"
                value={formData.contact_channel}
                onChange={(e) => handleInputChange("contact_channel", e.target.value)}
                isDisabled={loading}
              >
                {contactChannelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <FormHelperText>
                Como você entrou em contato com este lugar?
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Nome da Pessoa de Contato</FormLabel>
              <Input
                placeholder="Nome completo da pessoa"
                value={formData.contact_person_name}
                onChange={(e) => handleInputChange("contact_person_name", e.target.value)}
                isDisabled={loading}
                maxLength={255}
              />
              <FormHelperText>
                Nome da pessoa com quem você falou
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Observações do Contato</FormLabel>
              <Textarea
                placeholder="Descreva como foi o contato, o que foi discutido, interesse demonstrado..."
                value={formData.contact_notes}
                onChange={(e) => handleInputChange("contact_notes", e.target.value)}
                isDisabled={loading}
                rows={4}
              />
              <FormHelperText>
                Registre informações importantes da conversa
              </FormHelperText>
            </FormControl>

            <FormControl>
              <HStack justify="space-between" align="center">
                <VStack align="start" spacing={1}>
                  <FormLabel mb={0}>Precisa retornar contato?</FormLabel>
                  <Text fontSize="sm" color="gray.600">
                    Marque se ficou de retornar em uma data específica
                  </Text>
                </VStack>
                <Switch
                  isChecked={formData.should_return_contact}
                  onChange={(e) => handleInputChange("should_return_contact", e.target.checked)}
                  isDisabled={loading}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            {formData.should_return_contact && (
              <FormControl isRequired>
                <FormLabel>Data para Retorno</FormLabel>
                <Input
                  type="date"
                  value={formData.return_contact_date}
                  onChange={(e) => handleInputChange("return_contact_date", e.target.value)}
                  isDisabled={loading}
                  min={getTomorrowDate()}
                />
                <FormHelperText>
                  <HStack>
                    <Icon as={CalendarIcon} boxSize={3} />
                    <Text>Quando você deve retornar o contato?</Text>
                  </HStack>
                </FormHelperText>
              </FormControl>
            )}

            <Alert status="info" borderRadius="md" size="sm">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                Após registrar o contato, você poderá marcar como "Em Negociação"
                ou "Perdido" conforme o interesse demonstrado.
              </AlertDescription>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              onClick={handleClose}
              isDisabled={loading}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Salvando..."
              leftIcon={<PhoneIcon />}
            >
              Salvar Contato
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ContactModal;